/**
 *  @class SoulogMobileCreate
 *
 *  "Content pouring out of the phone screen" — 6 file cards take off flush with the screen plane, rising while turning upright.
 *  Plays once on entering the viewport; no pin, no scroll distance consumed (mobile only; desktop is handled by product-showcase-capture).
 *
 *  The key is "pose ownership" rather than displacement: on the first frame the cards fully inherit the base image plane's perspective tilt, are darker,
 *  and have no shadow of their own — they belong to the base image; with progress they turn upright, brighten, and grow a shadow — they come to belong to the viewer.
 *  With displacement alone, it would just read as "cards fading in out of nowhere and then drifting away".
 */
if (!customElements.get("soulog-mobile-create")) {
	const clamp = (v, a, b) => Math.min(b, Math.max(a, v));
	// Fall back only when missing/invalid; a valid 0 must survive
	const num = (v, dflt) => {
		const n = parseFloat(v);
		return Number.isFinite(n) ? n : dflt;
	};
	const easeOutCubic = (x) => 1 - Math.pow(1 - x, 3);

	class SoulogMobileCreate extends HTMLElement {
		connectedCallback() {
			this.stage = this.querySelector(".smc__stage");
			this.device = this.querySelector(".smc__device");
			this.portal = this.querySelector(".smc__portal");
			this.cards = Array.from(this.querySelectorAll(".smc__card"));
			if (!this.stage || !this.device || this.cards.length === 0) return;

			const d = this.dataset;
			this.p = {
				tilt: num(d.tilt, 24),
				twist: num(d.twist, -5),
				stagger: clamp(num(d.stagger, 0.5), 0, 0.9),
				duration: num(d.duration, 1500),
				emitX: num(d.emitX, 50),
				emitY: num(d.emitY, 65.4),
				arcLift: num(d.arcLift, 46),
			};

			this.cards.forEach((c, i) => {
				c._x = num(c.dataset.x, 50);
				c._y = num(c.dataset.y, 50);
				c._rot = num(c.dataset.rot, 0);
				c._delay = this.cards.length > 1 ? i / (this.cards.length - 1) : 0;
			});

			this.measure = this.measure.bind(this);
			this.measure();
			window.addEventListener("resize", this.measure);

			const qs = new URLSearchParams(window.location.search);

			// Freeze-frame mode: for screenshots / geometry acceptance checks, no animation
			const freeze = qs.get("smc-freeze");
			if (freeze !== null) {
				this.paint(clamp(num(freeze, 1), 0, 1));
				if (qs.get("smc-debug") === "1") this.report();
				return;
			}

			// reduced-motion: jump straight to the final state
			if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
				this.paint(1);
				if (qs.get("smc-debug") === "1") this.report();
				return;
			}

			this.paint(0);
			// The trigger observes the **stage** rather than the whole section: the whole section also includes the copy at the top, and computing the ratio on the whole section,
			// it could reach the threshold as soon as the copy peeks in, while the phone is still off-screen, and the cards would finish pouring out where no one can see.
			// Half the stage visible ≈ the phone has reached the middle of the screen, which is the right moment for "scrolled to half the screen".
			//
			// ⚠️ The threshold must be converted to "visible height", not hard-coded as a 0.5 ratio: the stage is a tall 390:620 box,
			// and once the viewport gets wider (tablet landscape) or shorter (landscape phone) it may be taller than the viewport, at which point intersectionRatio's
			// **maximum** can't even reach 0.5, the callback never fires, and the cards stay at opacity:0 forever — showing up as an entirely blank section.
			// The CSS side already caps the stage at 80vh at ≥768px; here we add another fallback: trigger when "half the stage" or
			// "half the screen" is visible, whichever is smaller.
			const vh = window.innerHeight || 800;
			const threshold = clamp(Math.min(this.H * 0.5, vh * 0.5) / Math.max(this.H, 1), 0.01, 0.5);
			this.observer = new IntersectionObserver(
				(entries) => {
					entries.forEach((e) => {
						if (!e.isIntersecting || this.played) return;
						this.played = true;
						this.play();
					});
				},
				{ threshold },
			);
			this.observer.observe(this.stage);

			// Re-arm after fully leaving the viewport, so scrolling back shows the pour-out again.
			// A separate threshold:0 observer: the one above is 0.5 and drops below it after a little scrolling,
			// so reusing it would reset the animation while the user is still watching.
			this.resetObserver = new IntersectionObserver(
				(entries) =>
					entries.forEach((e) => {
						if (e.isIntersecting || !this.played) return;
						this.played = false;
						if (this.raf) {
							cancelAnimationFrame(this.raf);
							this.raf = null;
						}
						this.paint(0);
					}),
				{ threshold: 0 },
			);
			this.resetObserver.observe(this.stage);
		}

		disconnectedCallback() {
			window.removeEventListener("resize", this.measure);
			if (this.observer) this.observer.disconnect();
			if (this.resetObserver) this.resetObserver.disconnect();
			if (this.raf) cancelAnimationFrame(this.raf);
		}

		measure() {
			this.W = this.stage.clientWidth;
			this.H = this.stage.clientHeight;
			this.ex = (this.p.emitX / 100) * this.W;
			this.ey = (this.p.emitY / 100) * this.H;
			this.cards.forEach((c) => {
				c._fx = (c._x / 100) * this.W;
				c._fy = (c._y / 100) * this.H;
			});
			if (this.t !== undefined) this.paint(this.t);
		}

		play() {
			const t0 = performance.now();
			const step = (now) => {
				const t = clamp((now - t0) / this.p.duration, 0, 1);
				this.paint(t);
				this.raf = t < 1 ? requestAnimationFrame(step) : null;
			};
			this.raf = requestAnimationFrame(step);
		}

		/** t ∈ [0,1]: 0 = not started, 1 = finished final frame */
		paint(t) {
			this.t = t;
			const P = this.p;

			// The base image **does not animate**: the phone is already there as you scroll in; cards pour out only once it's in position.
			// (An earlier version had the base image grow from small + tilt over, resulting in "scrolling down you first see a blank area" —
			//  the phone only appeared when the animation started. The pose is given once by a static CSS transform.)

			// The card phase spans the whole timeline
			const p = clamp(t, 0, 1);
			const denom = 1 - P.stagger;

			if (this.portal) {
				const up = clamp(p * 5, 0, 1);
				const down = 1 - clamp((p - 0.3) / 0.55, 0, 1);
				this.portal.style.opacity = (up * down * 0.85).toFixed(3);
			}

			// The arc's "push-out" distance scales with the viewport, otherwise the arc looks too pronounced on narrow screens
			const lift = P.arcLift * (this.W / 390);

			this.cards.forEach((c) => {
				const local = clamp((p - P.stagger * c._delay) / denom, 0, 1);
				const e = easeOutCubic(local);

				// Quadratic Bézier: the control point at the middle of the straight line is pushed outward along the screen normal, so the path is an arc rather than a straight line
				const cx = this.ex + (c._fx - this.ex) * 0.4;
				const cy = this.ey + (c._fy - this.ey) * 0.4 - lift;
				const bx = (1 - e) * (1 - e) * this.ex + 2 * (1 - e) * e * cx + e * e * c._fx;
				const by = (1 - e) * (1 - e) * this.ey + 2 * (1 - e) * e * cy + e * e * c._fy;

				c.style.transform =
					`translate(-50%,-50%) translate(${(bx - c._fx).toFixed(1)}px, ${(by - c._fy).toFixed(1)}px)` +
					` rotateX(${(P.tilt * (1 - e)).toFixed(2)}deg)` +
					` rotateZ(${(P.twist * (1 - e) + c._rot * e).toFixed(2)}deg)` +
					` scale(${(0.34 + 0.66 * e).toFixed(3)})`;
				// Reveal: in the first half it looks like being pushed out of a card slot
				c.style.clipPath = `inset(0 0 ${((1 - clamp(local / 0.5, 0, 1)) * 100).toFixed(1)}% 0 round 13px)`;
				// Brightness + shadow: from "belonging to the base image" to "belonging to the viewer"
				// ⚠️ filter applies before transform, so the shadow rotates along with the card — too large a vertical offset
				// gets turned into a diagonal offset, which reads like "a gray copy trailing behind every card". So the offset is kept to 4px,
				// and separation comes from more blur and lower density rather than displacement.
				c.style.filter =
					`brightness(${(0.74 + 0.26 * e).toFixed(3)})` +
					` drop-shadow(0 ${(4 * e).toFixed(1)}px ${(18 * e).toFixed(1)}px rgba(0,0,0,${(0.1 * e).toFixed(3)}))`;
				c.style.opacity = clamp(local * 2.6, 0, 1).toFixed(3);
			});
		}

		/**
		 * Geometry self-check (?smc-debug=1) — writes the verdict to <html data-smc-verdict>,
		 * so headless Chromium's --dump-dom can grep PASS / FAIL directly without parsing the console.
		 */
		report() {
			const SAFE = 20; // Mobile left/right safe gutter in px
			const fails = [];
			const stageBox = this.stage.getBoundingClientRect();
			const boxes = this.cards.map((c) => c.getBoundingClientRect());

			boxes.forEach((b, i) => {
				if (b.left < stageBox.left + SAFE - 0.5)
					fails.push(`card${i + 1}-left(${(b.left - stageBox.left).toFixed(1)}px)`);
				if (b.right > stageBox.right - SAFE + 0.5)
					fails.push(`card${i + 1}-right(${(stageBox.right - b.right).toFixed(1)}px)`);
				// Top and bottom must be checked too: the stage is overflow:hidden, so a card crossing the top/bottom edge gets clipped outright.
				// And getBoundingClientRect returns the box **not clipped by ancestors**, so when only left/right were checked,
				// a card pushed up to the stage's top edge with a corner clipped off still let every assertion pass (actually hit this).
				if (b.top < stageBox.top - 0.5) fails.push(`card${i + 1}-top(${(b.top - stageBox.top).toFixed(1)}px)`);
				if (b.bottom > stageBox.bottom + 0.5)
					fails.push(`card${i + 1}-bottom(${(stageBox.bottom - b.bottom).toFixed(1)}px)`);
			});

			// The overlap of any two cards must not swallow more than half of the smaller one, otherwise it reads as "piled into a lump"
			for (let i = 0; i < boxes.length; i++) {
				for (let j = i + 1; j < boxes.length; j++) {
					const a = boxes[i];
					const b = boxes[j];
					const ow = Math.max(0, Math.min(a.right, b.right) - Math.max(a.left, b.left));
					const oh = Math.max(0, Math.min(a.bottom, b.bottom) - Math.max(a.top, b.top));
					const minArea = Math.min(a.width * a.height, b.width * b.height);
					if (minArea > 0 && (ow * oh) / minArea > 0.55) fails.push(`overlap-${i + 1}x${j + 1}`);
				}
			}

			// Horizontal overflow is measured against the section itself, not documentElement — the section may be placed in any
			// container, and measuring the document would blame other elements' overflow on it, and also miss cases where it bursts its own container.
			// This also covers the heading: a long heading not wrapping and pushing past the right margin is the most likely thing to break here.
			if (this.scrollWidth > this.clientWidth + 1) fails.push(`h-overflow(${this.scrollWidth - this.clientWidth}px)`);

			document.documentElement.dataset.smcVerdict = fails.length ? `FAIL:${fails.join(",")}` : "PASS";
		}
	}

	customElements.define("soulog-mobile-create", SoulogMobileCreate);
}
